import type { CampaignWithComputed } from '../types/campaign';
import { parseJsonArray } from '../lib/jsonColumn';
import { calculateInfluencerCommission, getCampaignCommissionAmount, getCampaignTotalValue, type CreatorCommissionData } from './commissionCalculations';
import { getCampaignDeductionsTotal, getOutstandingDeductionsTotal } from './campaignExpenses';
import { isPastDue } from './dates';

interface ParsedInvoice {
  id?: string;
  status?: string;
  amount_received?: number;
  due_date?: string;
  invoice_number?: string;
  payment_received_date?: string;
}

interface ParsedInfluencerInvoice {
  id?: string;
  status?: string;
  amount?: number;
  source_name?: string;
  issue_date?: string;
  due_date?: string;
  payment_date?: string;
  payment_received_date?: string;
  funded_by_ids?: string[];
}

export function parseInfluencerInvoices(campaign: { influencer_invoices?: unknown }): ParsedInfluencerInvoice[] {
  return parseJsonArray<ParsedInfluencerInvoice>(campaign.influencer_invoices);
}

export function deriveInfluencerInvoiceStatus(campaign: {
  influencer_invoices?: unknown;
  influencer_invoice_status?: string | null;
}): string {
  const invoices = parseInfluencerInvoices(campaign);
  if (invoices.length === 0) {
    return campaign.influencer_invoice_status || 'Not Requested';
  }
  const allPaid = invoices.every((inv) => inv.status === 'Paid');
  if (allPaid) return 'Paid';
  const anyPaid = invoices.some((inv) => inv.status === 'Paid');
  const anyReceived = invoices.some((inv) => inv.status === 'Received');
  if (anyReceived || anyPaid) return 'Received';
  const anyRequested = invoices.some((inv) => inv.status === 'Requested');
  if (anyRequested) return 'Requested';
  return 'Not Requested';
}

export function parseMohitInvoices(campaign: { mohit_invoices?: unknown }): ParsedInvoice[] {
  return parseJsonArray<ParsedInvoice>(campaign.mohit_invoices);
}

export { parseCampaignExpenses, getCampaignDeductionsTotal } from './campaignExpenses';

export function deriveCompanyInvoiceStatus(campaign: { mohit_invoices?: unknown; company_invoice_status?: string | null }): string {
  const invoices = parseMohitInvoices(campaign);
  if (invoices.length === 0) {
    return campaign.company_invoice_status || 'Not Issued';
  }
  const allPaid = invoices.every((inv) => inv.status === 'Paid');
  if (allPaid) return 'Paid';
  const anyPaid = invoices.some((inv) => inv.status === 'Paid');
  const anyOverdue = invoices.some((inv) => inv.status === 'Overdue');
  if (anyOverdue) return 'Overdue';
  const anyIssued = invoices.some((inv) => inv.status === 'Issued');
  if (anyPaid || anyIssued) return 'Issued';
  return 'Not Issued';
}

export function sumAllMohitInvoiceAmounts(campaign: { mohit_invoices?: unknown }): number {
  const invoices = parseMohitInvoices(campaign);
  return invoices
    .filter((inv) => inv.status === 'Paid')
    .reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
}

export function sumAllInfluencerInvoiceAmounts(campaign: { influencer_invoices?: unknown }): number {
  const invoices = parseInfluencerInvoices(campaign);
  return invoices
    .filter((inv) => inv.status === 'Paid')
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);
}

export function getPaidReceivedAmount(campaign: { mohit_invoices?: unknown; company_amount_received?: number | string | null }): number {
  const invoices = parseMohitInvoices(campaign);
  if (invoices.length > 0) {
    const total = invoices
      .filter((inv) => inv.status === 'Paid')
      .reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
    if (total > 0) return total;
  }
  const fallback = campaign.company_amount_received;
  if (typeof fallback === 'string') return parseFloat(fallback) || 0;
  return fallback || 0;
}

export function getActualInfluencerPaidAmount(campaign: { influencer_invoices?: unknown }): number {
  const invoices = parseInfluencerInvoices(campaign);
  return invoices
    .filter((inv) => inv.status === 'Paid')
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);
}

/**
 * Overrides for campaigns whose payout terms don't live on campaign-level
 * fee/commission_pct (e.g. IM campaigns, where creators carry their own fees).
 */
export interface PayableOverrides {
  /** Total net amount owed to creator(s) when the campaign is fully funded. */
  totalNetEarnings: number;
  /** Blended commission rate applied to money received from the client. */
  commissionRate: number;
}

export function getPayableOverrides(campaign: {
  campaign_mode?: string | null;
  commission_amount?: number;
  net_fee_influencer?: number;
  campaign_expenses?: unknown;
}): PayableOverrides | undefined {
  if (campaign.campaign_mode !== 'influencer_marketing') return undefined;
  // Raw DB rows lack the computed IM fields — fall back to campaign-level math.
  if (typeof campaign.net_fee_influencer !== 'number') return undefined;
  const commission = campaign.commission_amount || 0;
  // net_fee_influencer is after deductions; the funded cap is before them,
  // or the payable math would withhold them twice
  const netEarnings = campaign.net_fee_influencer + getCampaignDeductionsTotal(campaign);
  const totalFee = netEarnings + commission;
  return {
    totalNetEarnings: netEarnings,
    commissionRate: totalFee > 0 ? commission / totalFee : 0,
  };
}

/**
 * Share of the campaign value we keep, derived from the real commission amount
 * rather than commission_pct. That way a flat-fee commission is honoured, and
 * a pass-through expense — charged to the client and owed to the creator, with
 * no commission on it — stays inside what we owe them.
 */
/** The campaign columns the payable math reads, raw row or computed. */
export interface PayableCampaignInput {
  campaign_mode?: string | null;
  has_payables?: boolean | null;
  mohit_invoices?: unknown;
  company_amount_received?: number | string | null;
  commission_pct?: number | null;
  commission_flat_fee?: number | null;
  commission_type?: string | null;
  commission_amount?: number;
  fee?: number | null;
  influencer_invoices?: unknown;
  campaign_expenses?: unknown;
  sponsorship_amount?: number | string | null;
  agency_commission_percent?: number | string | null;
}

export function getEffectiveCommissionRate(
  campaign: PayableCampaignInput,
  overrides?: PayableOverrides,
): number {
  if (overrides) return overrides.commissionRate;
  // Rate against everything the client is invoiced, so the pass-through part
  // of their payment flows to the creator untouched. Sponsorships resolve
  // through getCampaignTotalValue, so a raw row no longer yields a 0 total.
  const totalValue = getCampaignTotalValue(campaign);
  if (totalValue <= 0) return Number(campaign.commission_pct) || 0;
  const amount = getCampaignCommissionAmount(campaign);
  return Math.min(1, Math.max(0, amount / totalValue));
}

/**
 * Money-based payable: everything the client has actually paid (net of
 * commission) minus everything already paid out to the influencer(s).
 * Invoice counts and ordering are irrelevant — one influencer invoice may
 * cover several client tranches, and tranches may be paid out of order.
 */
export function getNetPayableAmount(campaign: PayableCampaignInput, overrides?: PayableOverrides): number {
  return getNetPayableBreakdown(campaign, overrides).netPayable;
}

export interface NetPayableBreakdown {
  /** Funded share owed to the creator before any deduction. */
  grossPayable: number;
  /** Outstanding deductions withheld on this payout. */
  deductionsApplied: number;
  /** What actually gets paid now. */
  netPayable: number;
}

/**
 * Same figure as getNetPayableAmount, split so the payments email can show
 * "Subtotal / (-) deduction / TOTAL A PAGAR" without subtracting twice.
 * Payable tranches are what the creator invoices (before deductions);
 * outstanding deductions come off the next payout and are then marked as
 * settled with it, so they are never withheld twice.
 */
export function getNetPayableBreakdown(
  campaign: PayableCampaignInput,
  overrides?: PayableOverrides,
): NetPayableBreakdown {
  const gross = getPaidReceivedAmount(campaign);
  if (gross <= 0) return { grossPayable: 0, deductionsApplied: 0, netPayable: 0 };

  const commissionRate = getEffectiveCommissionRate(campaign, overrides);
  const totalValue = getCampaignTotalValue(campaign);
  const netCap = overrides?.totalNetEarnings
    ?? (totalValue > 0 ? totalValue * (1 - commissionRate) : Infinity);
  const funded = Math.min(gross * (1 - commissionRate), netCap);

  const alreadyPaid = getActualInfluencerPaidAmount(campaign);
  const deductions = getOutstandingDeductionsTotal(campaign);

  const grossPayable = Math.max(0, funded - alreadyPaid);
  const deductionsApplied = Math.min(deductions, grossPayable);
  const netPayable = Math.max(0, grossPayable - deductions);
  return { grossPayable, deductionsApplied, netPayable };
}

export function isPaymentReady(campaign: PayableCampaignInput & {
  influencer_invoice_status?: string | null;
  net_fee_influencer?: number;
}): boolean {
  return getNetPayableAmount(campaign, getPayableOverrides(campaign)) > 0;
}

export interface InvoiceStats {
  totalReceived: number;
  netReceived: number;
  netPayable: number;
  paidInvoices: number;
  totalInvoices: number;
}

export function getInvoiceStats(campaign: CampaignWithComputed): InvoiceStats {
  const overrides = getPayableOverrides(campaign);
  const commissionPct = getEffectiveCommissionRate(campaign, overrides);
  const totalValue = getCampaignTotalValue(campaign);
  const netCap = overrides?.totalNetEarnings ?? (totalValue > 0 ? totalValue * (1 - commissionPct) : Infinity);
  // One source of truth for the payable so every view shows the same number
  const netPayable = getNetPayableAmount(campaign, overrides);
  const invoices = parseMohitInvoices(campaign);

  if (invoices.length > 0) {
    const paidOnly = invoices.filter((inv) => inv.status === 'Paid');
    const totalReceived = paidOnly.reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
    const netReceived = Math.min(totalReceived * (1 - commissionPct), netCap);
    return { totalReceived, netReceived, netPayable, paidInvoices: paidOnly.length, totalInvoices: invoices.length };
  }

  const fallback = typeof campaign.company_amount_received === 'string'
    ? parseFloat(campaign.company_amount_received) || 0
    : campaign.company_amount_received || 0;

  const netReceived = Math.min(fallback * (1 - commissionPct), netCap);
  return {
    totalReceived: fallback,
    netReceived,
    netPayable,
    paidInvoices: fallback > 0 ? 1 : 0,
    totalInvoices: 1,
  };
}

/**
 * Months ('YYYY-MM') of the payable invoices currently ready to pay.
 *
 * A payable is "ready" when it isn't paid yet and its funding has arrived:
 * with cross-check links, every linked client invoice must be Paid; without
 * links, a payable in status Received counts. The month comes from the
 * payable's due/issue date, falling back to when the linked client tranche
 * was actually paid.
 */
export function getReadyPayableMonths(campaign: {
  influencer_invoices?: unknown;
  mohit_invoices?: unknown;
}): string[] {
  const clientInvoices = parseMohitInvoices(campaign);
  const months = new Set<string>();

  for (const payable of parseInfluencerInvoices(campaign)) {
    if (payable.status === 'Paid') continue;

    const linkIds = payable.funded_by_ids || [];
    let monthSource: string | undefined;

    if (linkIds.length > 0) {
      const linked = clientInvoices.filter((c) => c.id && linkIds.includes(c.id));
      if (linked.length === 0 || !linked.every((c) => c.status === 'Paid')) continue;
      const latestPaymentDate = linked
        .map((c) => c.payment_received_date || '')
        .sort()
        .pop();
      monthSource = payable.due_date || payable.issue_date || latestPaymentDate;
    } else {
      if (payable.status !== 'Received') continue;
      monthSource = payable.due_date || payable.issue_date;
    }

    if (monthSource && monthSource.length >= 7) {
      months.add(monthSource.slice(0, 7));
    }
  }

  return Array.from(months).sort();
}

export interface OverdueInvoiceInfo {
  campaignId: string;
  invoiceNumber: string;
  dueDate: string;
  amount: number;
  status: string;
}

export function hasOverdueInvoices(campaign: {
  mohit_invoices?: unknown;
  company_invoice_status?: string | null;
  company_invoice_due_date?: string | null;
}): boolean {
  const invoices = parseMohitInvoices(campaign);
  if (invoices.length > 0) {
    return invoices.some((inv) => inv.status === 'Issued' && isPastDue(inv.due_date));
  }

  return campaign.company_invoice_status === 'Issued'
    && isPastDue(campaign.company_invoice_due_date);
}

export function getOverdueInvoices(campaign: {
  id?: string;
  campaign_mode?: string | null;
  mohit_invoices?: unknown;
  company_invoice_status?: string | null;
  company_invoice_due_date?: string | null;
  fee?: number | null;
  campaign_expenses?: unknown;
  sponsorship_amount?: number | string | null;
}): OverdueInvoiceInfo[] {
  const result: OverdueInvoiceInfo[] = [];

  const invoices = parseMohitInvoices(campaign);
  if (invoices.length > 0) {
    // Invoices carry their own value (hydrated rows always do). Only when an
    // older row has none do we fall back to an even share of what the client
    // is invoiced, which also works for sponsorships (fee 0 on the row).
    const totalValue = getCampaignTotalValue(campaign);
    const trancheCount = invoices.length;
    const perTranche = Math.round((totalValue / trancheCount) * 100) / 100;
    const lastTrancheAmount = Math.round((totalValue - perTranche * (trancheCount - 1)) * 100) / 100;

    invoices.forEach((inv, idx) => {
      if (inv.status !== 'Issued' || !inv.due_date) return;
      if (!isPastDue(inv.due_date)) return;
      const received = Number(inv.amount_received) || 0;
      const derived = idx === trancheCount - 1 ? lastTrancheAmount : perTranche;
      const amount = received > 0 ? received : derived;
      result.push({
        campaignId: campaign.id || '',
        invoiceNumber: inv.invoice_number || '-',
        dueDate: inv.due_date,
        amount,
        status: inv.status,
      });
    });
    return result;
  }

  if (
    campaign.company_invoice_status === 'Issued' &&
    campaign.company_invoice_due_date
  ) {
    if (isPastDue(campaign.company_invoice_due_date)) {
      result.push({
        campaignId: campaign.id || '',
        invoiceNumber: '-',
        dueDate: campaign.company_invoice_due_date,
        amount: getCampaignTotalValue(campaign),
        status: 'Issued',
      });
    }
  }

  return result;
}

// --- IM Per-Creator Allocation ---

export type AllocationMethod = 'exact' | 'proportional';

export interface CreatorPaymentAllocation {
  creatorName: string;
  agreedFee: number;
  commission: number;
  netEarnings: number;
  amountPaid: number;
  netPayable: number;
  method: AllocationMethod;
}

export function getCreatorPaidAmount(
  campaign: { influencer_invoices?: unknown },
  creatorName: string
): number {
  const invoices = parseInfluencerInvoices(campaign);
  return invoices
    .filter((inv) => inv.status === 'Paid' && inv.source_name === creatorName)
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);
}

/**
 * Determines the funded gross amount for a single creator.
 * EXACT path: uses allocated_amount from invoice_links (capped at actual received).
 * PROPORTIONAL fallback: splits totalReceived by fee ratio.
 */
export function computeCreatorFundedGross(
  totalCampaignReceived: number,
  creatorAgreedFee: number,
  totalCampaignAgreedFees: number,
  exactAllocatedAmount: number | null,
): { fundedGross: number; method: AllocationMethod } {
  // EXACT: invoice_links allocated_amount, capped at what was actually received
  if (exactAllocatedAmount !== null && exactAllocatedAmount > 0) {
    const capped = Math.min(exactAllocatedAmount, totalCampaignReceived);
    return { fundedGross: capped, method: 'exact' };
  }
  // PROPORTIONAL: split by fee ratio
  const ratio = totalCampaignAgreedFees > 0 ? creatorAgreedFee / totalCampaignAgreedFees : 0;
  return { fundedGross: totalCampaignReceived * ratio, method: 'proportional' };
}

/**
 * Computes net received for a creator from their funded gross.
 * Uses per-creator commission rate (commissionAmount / agreedFee), capped at netEarnings.
 */
export function computeCreatorNetReceived(
  fundedGross: number,
  commissionAmount: number,
  agreedFee: number,
  netEarnings: number,
): number {
  const commissionRate = agreedFee > 0 ? commissionAmount / agreedFee : 0;
  const netFromFunded = fundedGross * (1 - commissionRate);
  return Math.min(netFromFunded, netEarnings);
}

/**
 * Full allocation for multiple creators within a campaign.
 * exactAllocations: Map<creatorName, allocatedAmount from invoice_links> (null if no links exist).
 */
export function getCreatorPaymentAllocations(
  campaign: { mohit_invoices?: unknown; company_amount_received?: number; influencer_invoices?: unknown },
  creators: (CreatorCommissionData & { name: string })[],
  exactAllocations?: Map<string, number | null>,
): CreatorPaymentAllocation[] {
  if (creators.length === 0) return [];

  const totalAgreedFees = creators.reduce((sum, c) => sum + (c.agreed_fee || 0), 0);
  const totalReceived = getPaidReceivedAmount(campaign);

  return creators.map((creator) => {
    const agreedFee = creator.agreed_fee || 0;
    const commission = calculateInfluencerCommission(
      agreedFee,
      creator.commission_pct ?? 0,
      creator.commission_flat_fee ?? 0,
      creator.commission_type ?? 'percentage'
    );
    const netEarnings = agreedFee - commission;

    const amountPaid = getCreatorPaidAmount(campaign, creator.name);

    const exactAlloc = exactAllocations?.get(creator.name) ?? null;
    const { fundedGross, method } = computeCreatorFundedGross(
      totalReceived, agreedFee, totalAgreedFees, exactAlloc
    );

    const netReceived = computeCreatorNetReceived(fundedGross, commission, agreedFee, netEarnings);
    const netPayable = Math.max(0, netReceived - amountPaid);

    return {
      creatorName: creator.name,
      agreedFee,
      commission,
      netEarnings,
      amountPaid,
      netPayable,
      method,
    };
  });
}

export function isCreatorPaymentReady(
  campaign: { mohit_invoices?: unknown; company_amount_received?: number; influencer_invoices?: unknown },
  creatorName: string,
  agreedFee: number,
  commissionPct: number,
  commissionFlatFee: number,
  commissionType: 'percentage' | 'flat_fee'
): boolean {
  const totalReceived = getPaidReceivedAmount(campaign);
  if (totalReceived <= 0) return false;

  const commission = calculateInfluencerCommission(agreedFee, commissionPct, commissionFlatFee, commissionType);
  const netEarnings = agreedFee - commission;
  const amountPaid = getCreatorPaidAmount(campaign, creatorName);

  return amountPaid < netEarnings && totalReceived > 0;
}
